import { computed, Injectable, Signal, signal, TemplateRef } from '@angular/core';
import { Observable, Subject } from 'rxjs';

import { AnimationService } from 'src/app/infrastructure/services/loading-services/animation.service';
import { _CentralPageService } from './_central-page.service';
import { IImageParentData, imageParentData } from 'src/app/features/image/models/image-parent-data';
import { NavigationHistoryService } from 'src/app/features/shared/navigation-history/services/navigation-history.service';
import { navCategory, TNavCategory } from 'src/app/features/shared/navigation-history/models/nav-history-entry';
import { PagerMode } from '../../pager/model/pager-show-mode';
import { sectionLayout, TSectionLayout } from 'src/app/infrastructure/models/section-layout';
import { TabPageStorageService } from '../../storage/services/storage-services/tab-page-storage.service';
import { _FormModelService } from 'src/app/infrastructure/services/model-services/_form-model.service';
import { _PageService } from 'src/app/infrastructure/services/page-services/_page.service';
import { injectRequired } from 'src/app/infrastructure/functions/inject-required.function';
import { pageMode } from 'src/app/infrastructure/models/page-mode';
import { routeActionParam } from 'src/app/infrastructure/services/page-services/models/return-page-query-param';
import { routePage } from 'src/app/infrastructure/services/route-service/models/route-page';
import { TPhotoName } from 'src/app/infrastructure/models/image-type';
import { ITabOwnerClaim } from '../models/tab-data-template';
import { ClaimPage } from 'src/app/infrastructure/models/claim-page';
import { claimName, TClaimName } from 'src/assets/common/enums/claim-name.g';

@Injectable()
export abstract class _TabPageService
    extends _CentralPageService
{
    protected readonly _animationService = injectRequired(AnimationService);
    protected readonly _formModelService = injectRequired(_FormModelService);
    protected readonly _navHistoryService = injectRequired(NavigationHistoryService);
    protected readonly _pageService = injectRequired(_PageService);
    protected readonly _tabPageStorageService = injectRequired(TabPageStorageService);

    abstract readonly pagerMode$$: Signal<PagerMode>;
    abstract readonly animating$$: Signal<boolean>;
    abstract readonly photoShow$$: Signal<boolean>;
    abstract readonly photoGuid$$: Signal<string | null>;
    abstract readonly photoVersion$$: Signal<string | null>;
    abstract readonly photoDropEnabled$$: Signal<boolean>;
    abstract readonly photoDrop$: Observable<unknown>;
    abstract readonly photoUpload$: Observable<File>;
    abstract readonly photoListChanged$: Observable<void>;
    abstract readonly sectionCompact_template$$: Signal<TemplateRef<any> | null>;

    protected _animating$$ = this._animationService.isAnimating$$;
    protected _photoShow$$ = signal<boolean>(false);
    protected _sectionLayout$$ = signal<TSectionLayout>(sectionLayout.form);

    // Optimistische hoofdfoto-override: sinds de generieke mainPhoto_change() hieronder de ENE
    // ingang is voor elke hoofdfoto-wijziging, eigent deze service het override-signaal nu zelf aan
    // (geen source-signal-indirectie meer nodig — mainPhoto_change is een normale methode-aanroep,
    // geen effect/computed die niet naar eigen signals mag schrijven). De override-waarde is
    // drieledig: undefined = geen override actief (val terug op de DTO), null = expliciet "geen
    // hoofdfoto" (na verwijderen — juist NIET terugvallen op de DTO), een guid = die foto
    // optimistisch tonen.
    private __photoGuidOverride$$ = signal<string | null | undefined>(undefined);
    // Cache-bust-versie die bij de override hoort (zie mainPhoto_change). Een "andere" guid is níet
    // per se ongecacht: een eerder gebruikte (en evt. bewerkte) foto opnieuw als hoofdfoto kiezen
    // toonde zonder versie de oude, gecachte inhoud.
    private __photoVersionOverride$$ = signal<string | null>(null);

    // Transiënte hover-preview tijdens het slepen van een thumbnail boven de hoofdfoto-box (zie
    // photoDragPreview_set hieronder) — los van __photoGuidOverride$$ hierboven, die is voor de
    // PERMANENTE state ná een geslaagde drop/PATCH. null ⇒ niet aan het hoveren.
    private __photoDragPreviewGuid$$ = signal<string | null>(null);
    private __photoDragPreviewVersion$$ = signal<string | null>(null);

    protected _photoGuid$$ = computed(() => {
        const __preview = this.__photoDragPreviewGuid$$();
        if (__preview !== null) return __preview;

        const __value = this.__photoGuidOverride$$();
        return __value !== undefined
            ? __value
            : (this._formModelService.dataModel$$() as IImageParentData)?.[imageParentData.photoThumbGuid] ?? null;
    });

    // Cache-bust-versie voor de hoofdfoto-box. Tijdens een actieve drag-preview of optimistische
    // override: de versie die daarbij is meegegeven (de modifiedOn van het gekozen lijst-item; null
    // bij een verse upload of na verwijderen). Anders de photoVersion (modifiedOn) uit het model, zodat
    // een in-place annotatie/vervang de cache bust.
    protected _photoVersion$$ = computed(() => {
        if (this.__photoDragPreviewGuid$$() !== null) return this.__photoDragPreviewVersion$$();
        if (this.__photoGuidOverride$$() !== undefined) return this.__photoVersionOverride$$();
        return (this._formModelService.dataModel$$() as IImageParentData)?.[imageParentData.photoVersion] ?? null;
    });

    // True zolang een thumbnail boven de hoofdfoto-box hangt — de box gebruikt dit voor een visuele
    // 'geldig drop-doel'-hint (zie image-box-details.component.html/scss). Los van CDK's eigen
    // cdk-drop-list-receiving-mechanisme: dat bleek de hint juist te doven zodra je er ECHT overheen
    // hing (zie photoDragPreview_set hieronder).
    photoDragActive$$ = computed(() => this.__photoDragPreviewGuid$$() !== null);

    // 'Mag slepen naar hoofdfoto' + het drop-event. De hoofdfoto-box is drop-target zolang minstens
    // één fotolijst in hoofdfoto-modus staat (zie photoDropTarget_set, aangeroepen door
    // ImageTablistComponent) — en die modus is alleen bereikbaar met de lijst-claim BtnMainPhotoEdit_Show
    // op <X>Photos. Eén bron van waarheid: slepen en checkbox delen zo exact hetzelfde recht, i.p.v.
    // een losse container-check die ervan kon afwijken. Registratie per lijst (Set), zodat twee
    // gelijktijdig gemounte lijsten (bv. DomainUser) elkaar niet overschrijven.
    private __photoDropTargets$$ = signal<ReadonlySet<object>>(new Set());
    protected _photoDropEnabled$$ = computed(() => this.__photoDropTargets$$().size > 0);
    protected _photoDrop$ = new Subject<unknown>();

    // Foto geüpload via de hoofdfoto-box zelf (i.p.v. gesleept): de parent verwerkt het bestand
    // (resize + /api/Photo/image-save) en koppelt het resultaat via /api/Photo/main, zelfde als
    // een drop. Gescheiden subject van _photoDrop$ omdat de payload een File is, geen lijst-item.
    protected _photoUpload$ = new Subject<File>();

    // Meldt dat de fotolijst van de huidige pagina gewijzigd is buiten de *PhotoTablist zelf om (nu:
    // een geslaagde hoofdfoto-box-upload). Een al-gemounte tablist-tab wordt door Angular Material
    // niet herbouwd zolang hij de geselecteerde ('gecentreerde') tab blijft — needRefresh alleen
    // zetten (tabList_setRefresh) wordt dan nooit meer gelezen. Dit signaal laat de tablist zelf,
    // ongeacht zichtbaarheid, meteen herladen.
    protected _photoListChanged$ = new Subject<void>();

    // Origin (volledige) guid van de hoofdfoto — photoGuid$$ is de thumb-variant die de box toont.
    // Navigatie naar ImageDetailsComponent heeft de origin-guid nodig (valt terug op de thumb als
    // er (nog) geen aparte origin-variant bekend is).
    protected _photoGuidOrigin$$ = computed(() =>
        (this._formModelService.dataModel$$() as IImageParentData)?.[imageParentData.photoOriginGuid]
            ?? this._photoGuid$$()
    );

    // FileType van de hoofdfoto (bv. 'orderPhoto'), eenmalig aangeleverd door de concrete
    // pagina-component (zie photoType_set) — nodig als imageType-queryparam bij navigatie.
    private __photoType$$ = signal<TPhotoName | null>(null);

    // Nav-category van deze pagina (zie mainPhoto_change), eenmalig aangeleverd door de concrete
    // pagina-component (zie navCategory_set) — laat mainPhoto_change ook de navigatiehistorie-
    // drawer-entry bijwerken. null (default) voor pagina's zonder drawer-entry (bv. admin-only).
    private __navCategory$$ = signal<TNavCategory | null>(null);

    protected _template_sectionCompact$$ = signal<TemplateRef<any> | null>(null);
    protected _pagerMode$$ = signal<PagerMode>('hidden');

    // True als de huidige tab een paneel is (statische inhoud, geen lijst/footer), bv. Theme; false = lijst-tab.
    abstract readonly tabIsPanel$$: Signal<boolean>;

    sectionLayout$$ = this._sectionLayout$$.asReadonly();

    // Upload-recht van de hoofdfoto-box (camera/galerij), generiek voor élke hoofdfoto: claim
    // BtnMainPhotoEdit_Show op de details-sectie van deze pagina (pageName_sectionDetails$$, geregistreerd
    // via CLAIM_PAGE_DETAILS), óf dezelfde claim via de owner-bypass als de pagina een eigenaar opgeeft
    // (photoOwnerClaimSource_set, bv. DomainUser = EntityOwner). Fail-closed: zonder claim geen upload.
    // Zelfde tonen/opslaan-scheiding als elders: BtnMainPhotoEdit_Show = FE-zichtbaarheid, de backend
    // checkt bij /api/Photo/main zelf MainPhoto_Update. Een pagina kan bewust afwijken via
    // photoReadOnly_set (bv. ProjectMember: profielfoto hoort bij de DomainUser, daar beheerd).
    // Zet een pagina zonder domain-scope (Home) hierop: tab- én hoofdfoto-autorisatie gebruiken dan
    // geen domain-scope (routeKeys kan anders een domain-fallback van een eerder bezoek meedragen).
    tabAuth_noDomainScope = false;

    private __photoOwnerClaimSrc$$ = signal<Signal<ITabOwnerClaim | null> | null>(null);
    private __photoUploadCondition$$ = signal<Signal<boolean> | null>(null);
    private __photoReadOnly$$ = signal<boolean>(false);
    photoReadOnly$$ = this.__photoReadOnly$$.asReadonly();

    photoCanUpload$$ = computed(() => {
        if (this.__photoReadOnly$$()) return false;
        if (this.__photoUploadCondition$$()?.() === false) return false;

        return this.mainPhotoClaim_check(this.pageName_sectionDetails$$(), this.__photoOwnerClaimSrc$$()?.() ?? null);
    });

    // Doorklikken van de hoofdfoto-box naar ImageDetails: zelfde generieke regel als de upload, met
    // claim BtnImageDetails_Show op de details-sectie (of via dezelfde owner-bypass). Fail-closed. ImageDetails
    // bepaalt daarna zelf of je mag bewerken/verwijderen (PhotoMetadataDto.editPage/deletePage).
    photoCanOpen$$ = computed(() => {
        if (this.__photoReadOnly$$()) return false;
        return this.mainPhotoClaim_check(this.pageName_sectionDetails$$(), this.__photoOwnerClaimSrc$$()?.() ?? null, claimName.btnImageDetails_Show);
    });

    photoOwnerClaimSource_set(source: Signal<ITabOwnerClaim | null>): void {
        this.__photoOwnerClaimSrc$$.set(source);
    }

    // Extra, pagina-specifieke voorwaarde bovenop de claim (EN), bv. Notice: niet bij lock.
    photoUploadConditionSource_set(source: Signal<boolean>): void {
        this.__photoUploadCondition$$.set(source);
    }

    // Box volledig inert (geen upload, geen klik naar ImageDetails), ongeacht claims — voor een pagina
    // die de foto uitsluitend ter herkenning toont (bv. ProjectMember).
    photoReadOnly_set(value: boolean): void {
        this.__photoReadOnly$$.set(value);
    }

    photoShow_set(value: boolean): void {
        this._photoShow$$.set(value);
    }

    photoDropTarget_set(owner: object, active: boolean): void {
        const __current = this.__photoDropTargets$$();
        if (__current.has(owner) === active) return;
        const __next = new Set(__current);
        if (active) __next.add(owner); else __next.delete(owner);
        this.__photoDropTargets$$.set(__next);
    }

    // Generieke hoofdfoto-claimcheck (default BtnMainPhotoEdit_Show op `page`, of via de owner-bypass) —
    // gedeeld door de banner (photoCanUpload$$/photoCanOpen$$, page = <X>Details) en de fotolijst
    // (ImageTablistComponent, page = <X>Photos), zodat alle hoofdfoto-acties exact dezelfde regel volgen.
    mainPhotoClaim_check(page: ClaimPage | null, owner: ITabOwnerClaim | null, claim: TClaimName = claimName.btnMainPhotoEdit_Show): boolean {
        if (!page) return false;
        if (this._applicationUserService.claim_check(
            page, this.tabAuth_noDomainScope ? null : this._activatedRouteService.routeKeys, claim,
            this._applicationUserService.userClaims$$(),
        )) return true;
        return !!owner?.ownerGuid
            && this._applicationUserService.ownerClaim_check(owner.kind, owner.ownerGuid, page, claim);
    }
    photoDrop_emit(item: unknown): void {
        this._photoDrop$.next(item);
    }
    photoUpload_emit(file: File): void {
        this._photoUpload$.next(file);
    }
    photoListChanged_emit(): void {
        this._photoListChanged$.next();
    }

    photoType_set(value: TPhotoName): void {
        this.__photoType$$.set(value);
    }

    navCategory_set(value: TNavCategory): void {
        this.__navCategory$$.set(value);
    }

    // Aangeroepen door ImageTablistComponent tijdens het slepen (eigen pointer-vs-boundingrect-
    // hittest, zie __isOverMainBox daar) i.p.v. CDK's cdkDropListConnectedTo — een cross-list-
    // transfer verplaatste de gesleepte tegel daadwerkelijk (DOM) naar de hoofdfoto-drop-zone zodra
    // je erover hing, met twee glitches tot gevolg: de tegel verdween tijdelijk uit de fotolijst, én
    // de 'geldig-drop-doel'-rand van de hoofdfoto-box doofde juist op dat moment (CDK's `exit()` op
    // de bronlijst reset ook de 'receiving'-status van alle verbonden lijsten, incl. de hoofdfoto-box
    // zelf — zie Angular CDK drag-drop.mjs DropListRef.exit()/_reset()/_stopReceiving()). guid null
    // ⇒ niet (meer) aan het hoveren; de box valt dan terug op de echte hoofdfoto.
    photoDragPreview_set(guid: string | null, version: string | null = null): void {
        this.__photoDragPreviewVersion$$.set(guid ? version : null);
        this.__photoDragPreviewGuid$$.set(guid);
    }

    // ÉÉN generieke ingang voor "de hoofdfoto is gewijzigd" — drop, checkbox, upload via de
    // hoofdfoto-box, een foto-picker in een edit-form, … Zet meteen de optimistische banner-
    // override (zelfde drieledige semantiek als hierboven; undefined draait de override terug naar
    // de DTO-waarde, bv. na een mislukte PATCH) én patcht, als deze pagina een nav-category heeft
    // (navCategory_set), de eventuele navigatiehistorie-drawer-entry — zodat de foto overal waar hij
    // getoond wordt in sync blijft zonder dat elke aanroeper dat zelf apart moet doen (zie ook
    // ImageTablistComponent, dat dit voor de eigen-hoofdfoto-flow rechtstreeks aanroept).
    // version: de cache-bust-versie van de nieuwe foto (lijst-item.version) — zie __photoVersionOverride$$.
    mainPhoto_change(newGuid: string | null | undefined, version: string | null = null): void {
        this.__photoVersionOverride$$.set(newGuid ? version : null);
        this.__photoGuidOverride$$.set(newGuid);

        const __category = this.__navCategory$$();
        if (!__category) return;

        const __model = this._formModelService.dataModel$$() as ({ guid?: string | null } & Partial<IImageParentData>) | null;
        const __guid = __model?.guid ?? null;
        if (!__guid) return;

        const __effectiveGuid = newGuid !== undefined ? newGuid : (__model?.[imageParentData.photoThumbGuid] ?? null);

        // Een expliciete, niet-lege keuze op de DomainUser-pagina zelf is per definitie een eigen
        // domain-specifieke profielfoto (geen fallback meer) — anders zou een latere Home-
        // hoofdfoto-wijziging (zie NavigationHistoryService.userPhotoGuid_propagate) deze entry
        // straks ten onrechte weer overschrijven. Overige categorieën/aanroepers kennen dit
        // fallback-concept niet: undefined laat de vlag daar ongemoeid.
        const __photoIsUserFallback = __category === navCategory.domainUsers && __effectiveGuid !== null
            ? false
            : undefined;
        this._navHistoryService.photoGuid_update(__category, __guid, __effectiveGuid, __photoIsUserFallback);
    }

    // Klik op de hoofdfoto-box: open ImageDetailsComponent, zelfde route/params-patroon als de
    // fotolijst-tegels (zie image-tablist.emit_onClick). Geen navigatie zonder guid (nog geen
    // hoofdfoto gekozen).
    photo_navigate(): void {
        const __key = this._photoGuidOrigin$$();
        if (!__key) return;

        const __routeKeys = this._activatedRouteService.routeKeys;
        const __query: { [key: string]: string } = {};
        const __imageType = this.__photoType$$();
        if (__imageType) __query['imageType'] = __imageType;
        const __version = this._photoVersion$$();
        if (__version) __query['v'] = __version;

        this._pageService.page_goto(
            routePage.image,
            { key: __key, dauGuid: __routeKeys?.dauGuid ?? undefined, pamGuid: __routeKeys?.pamGuid ?? undefined },
            [routeActionParam.keep],
            Object.keys(__query).length ? __query : undefined,
        );
    }

    sectionLayout_set(value: TSectionLayout): void {
        this._sectionLayout$$.set(value);
    }

    tabShowClick(): void {
        const _pageMode = this.pageMode$$();

        if (_pageMode == pageMode.detailsPage || _pageMode == pageMode.editPage) {
            this.pageMode_set(pageMode.tabPage);
        }
    }

    template_sectionCompact_set(templateRef: TemplateRef<any> | undefined) {
        if (templateRef != null) {
            this._template_sectionCompact$$.set(templateRef);
        }
    }
}
